// Verified, static reference data transcribed directly from
// unified-profiles' storage/src/main/kotlin/com/meltwater/unified/profiles/linking/
// application/ProfileLinkMetadataScorer.kt and core/ImageEvidence.kt.
// Every value here is copied from a real code branch — nothing is invented.
// Where the evaluator has no explicit penalty/bonus branch for a case, that
// is stated as-is rather than guessed at.

export interface ScoringRuleRow {
  signal: string;
  condition: string;
  impact: string;
}

export const HANDLE_SIMILARITY_BASE: ScoringRuleRow[] = [
  { signal: 'Handle similarity (base score)', condition: 'Exact match', impact: '0.60' },
  { signal: '', condition: 'Very close (1 character different)', impact: '0.40' },
  { signal: '', condition: 'Close (2 characters different)', impact: '0.30' },
  { signal: '', condition: 'No close match', impact: '0.10' },
];

// Numeric form of the table above, for computing the transparent breakdown
// (base + metadata signal contributions = metadata score) without needing
// a new backend field — the base score is a pure function of matchType.
export const HANDLE_MATCH_BASE_SCORE: Record<'EXACT' | 'LEV1' | 'LEV2' | 'NOT_FOUND', number> = {
  EXACT: 0.6,
  LEV1: 0.4,
  LEV2: 0.3,
  NOT_FOUND: 0.1,
};

export const METADATA_SIGNAL_RULES: ScoringRuleRow[] = [
  { signal: 'Gender', condition: 'Same', impact: '+0.10' },
  { signal: '', condition: 'Different', impact: '−0.10' },
  { signal: 'Country', condition: 'Same', impact: '+0.10' },
  { signal: '', condition: 'Different', impact: 'No penalty — 0.00' },
  { signal: 'Follower scale', condition: 'Within ~10x of each other', impact: '+0.05' },
  { signal: '', condition: 'Very different', impact: 'No penalty — 0.00' },
  { signal: 'Post volume', condition: 'Within ~10x of each other', impact: '+0.05' },
  { signal: '', condition: 'Very different', impact: 'No penalty — 0.00' },
  { signal: 'Verified status', condition: 'Both verified', impact: '+0.05' },
  { signal: '', condition: 'Otherwise', impact: 'No penalty — 0.00' },
  { signal: 'Display name', condition: 'Match', impact: '+0.10' },
  { signal: '', condition: 'No match', impact: 'No penalty — 0.00' },
  { signal: 'First name', condition: 'Contradicts (e.g. clearly different first name)', impact: '−0.10' },
  { signal: '', condition: 'No contradiction', impact: 'No bonus — 0.00' },
  { signal: 'Account type', condition: 'Contradicts (e.g. person vs. non-person)', impact: '−0.05' },
  { signal: '', condition: 'No contradiction', impact: 'No bonus — 0.00' },
  { signal: 'Bio text', condition: 'High similarity (≥0.75)', impact: '+0.15' },
  { signal: '', condition: 'Good similarity (≥0.60)', impact: '+0.10' },
  { signal: '', condition: 'Some similarity (≥0.45)', impact: '+0.05' },
  { signal: '', condition: 'Low similarity', impact: 'No penalty — 0.00' },
  { signal: 'Language', condition: 'Match', impact: '+0.05' },
  { signal: '', condition: 'No match', impact: 'No penalty — 0.00' },
  { signal: 'Email', condition: 'Match', impact: '+0.10' },
  { signal: '', condition: 'No match', impact: 'No penalty — 0.00' },
  { signal: 'Website', condition: 'Match', impact: '+0.05' },
  { signal: '', condition: 'No match', impact: 'No penalty — 0.00' },
  { signal: 'Occupation', condition: 'Overlapping text', impact: '+0.05' },
  { signal: '', condition: 'No overlap', impact: 'No penalty — 0.00' },
];

export const IMAGE_ADJUSTMENT_RULES: ScoringRuleRow[] = [
  { signal: 'Strong image match', condition: '', impact: '+0.20' },
  { signal: 'Inconclusive', condition: '', impact: '0.00' },
  { signal: 'Strong image mismatch', condition: '', impact: '−0.10' },
  { signal: 'Not compared (image missing/unavailable)', condition: '', impact: '0.00' },
];

// Raw SignalContribution.name values with a verified rule above — kept in
// sync manually with METADATA_SIGNAL_RULES since the wire uses raw keys
// while the table above uses friendly labels.
export const KNOWN_SIGNAL_NAMES = new Set([
  'gender',
  'country',
  'followerMagnitude',
  'postMagnitude',
  'verified',
  'displayName',
  'firstNameMismatch',
  'accountType',
  'bioText',
  'language',
  'email',
  'website',
  'occupation',
]);

export const FALLBACK_RULE_NOTE = 'Contribution determined by profile-linking evaluation model.';
